/**
 * CreateDossierWizard — 4-step wizard to create a new dossier.
 * Admin-only. Accessible as a standalone page at /admin/nouveau-dossier.
 *
 * Steps:
 * 1. Contacts — search/create customers, assign roles (multiple contacts)
 * 2. Property — address, type
 * 3. Mission — type, sub-type, notes
 * 4. Confirm — summary, create
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '@/core/api/client';

// Types
interface CustomerResult {
  id: string;
  type: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  phone_mobile: string | null;
  vat_number: string | null;
  address_city: string | null;
}

interface LinkedContact {
  customer: CustomerResult;
  roles: string[];
  is_billing_contact: boolean;
  is_site_contact: boolean;
}

interface WizardData {
  contacts: LinkedContact[];
  property_street: string;
  property_number: string;
  property_postcode: string;
  property_city: string;
  property_type: string;
  type: string;
  sub_type: string;
  notes: string;
}

const INITIAL_DATA: WizardData = {
  contacts: [],
  property_street: '',
  property_number: '',
  property_postcode: '',
  property_city: '',
  property_type: 'maison',
  type: 'expertise_bancaire',
  sub_type: 'FULL EXPERTISE',
  notes: '',
};

const CONTACT_ROLES = [
  { value: 'demandeur', label: 'Demandeur' },
  { value: 'intermediaire', label: 'Intermédiaire' },
  { value: 'proprietaire', label: 'Propriétaire' },
  { value: 'apporteur', label: 'Apporteur' },
  { value: 'notaire', label: 'Notaire' },
  { value: 'banque', label: 'Banque' },
  { value: 'expert', label: 'Expert' },
  { value: 'locataire', label: 'Locataire' },
];

const PROPERTY_TYPES = [
  { value: 'maison', label: 'Maison' },
  { value: 'appartement', label: 'Appartement' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'garage', label: 'Garage' },
  { value: 'bureau', label: 'Bureau' },
  { value: 'entrepot', label: 'Entrepôt' },
  { value: 'autre', label: 'Autre' },
];

const MISSION_TYPES = [
  { value: 'expertise_bancaire', label: 'Expertise bancaire' },
  { value: 'expertise_privee', label: 'Expertise privée' },
  { value: 'avis_valeur', label: 'Avis de valeur' },
  { value: 'expertise_judiciaire', label: 'Expertise judiciaire' },
  { value: 'autre', label: 'Autre' },
];

const STEPS = [
  { id: 1, label: 'Contacts', icon: UserIcon },
  { id: 2, label: 'Bien', icon: HomeIcon },
  { id: 3, label: 'Mission', icon: ClipboardDocumentListIcon },
  { id: 4, label: 'Confirmer', icon: CheckCircleIcon },
];

const customerDisplayName = (c: CustomerResult | null) => {
  if (!c) return '';
  if (c.company_name) return c.company_name;
  return [c.first_name, c.last_name].filter(Boolean).join(' ');
};

// ============================================
// Inline Customer Create Form
// ============================================
const InlineCreateForm: React.FC<{
  onCreated: (c: CustomerResult) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({
    type: 'person' as 'person' | 'company',
    first_name: '', last_name: '', company_name: '',
    email: '', phone: '', vat_number: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (form.type === 'person' && !form.last_name) { setError('Nom requis'); return; }
    if (form.type === 'company' && !form.company_name) { setError('Nom de société requis'); return; }
    setSaving(true);
    setError('');
    try {
      const res: any = await apiClient.post('/api/customers', { ...form, force_create: true });
      if (res.success) onCreated(res.data);
      else setError(res.message || 'Erreur');
    } catch (e: any) {
      setError(e.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="create-dossier-inline-create">
      <div className="create-dossier-inline-create-header">
        <h4>Nouveau client</h4>
        <button onClick={onCancel} className="create-dossier-inline-close"><XMarkIcon className="w-4 h-4" /></button>
      </div>

      <div className="create-dossier-inline-type-toggle">
        <button className={`toggle-btn ${form.type === 'person' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, type: 'person' }))}>Personne</button>
        <button className={`toggle-btn ${form.type === 'company' ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, type: 'company' }))}>Société</button>
      </div>

      <div className="create-dossier-form">
        {form.type === 'person' ? (
          <div className="create-dossier-form-row-split">
            <div>
              <label>Prénom</label>
              <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Jean" />
            </div>
            <div>
              <label>Nom *</label>
              <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Dupont" />
            </div>
          </div>
        ) : (
          <>
            <div>
              <label>Nom de société *</label>
              <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="SRL Example" />
            </div>
            <div>
              <label>N° TVA</label>
              <input value={form.vat_number} onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))} placeholder="BE0123456789" />
            </div>
          </>
        )}
        <div className="create-dossier-form-row-split">
          <div>
            <label>Email</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
          </div>
          <div>
            <label>Téléphone</label>
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+32..." />
          </div>
        </div>
      </div>

      {error && <div className="create-dossier-error">{error}</div>}

      <div className="create-dossier-inline-actions">
        <button onClick={onCancel} className="create-dossier-nav-btn create-dossier-nav-btn--secondary">Annuler</button>
        <button onClick={handleSave} disabled={saving} className="create-dossier-nav-btn create-dossier-nav-btn--primary">
          {saving ? 'Création...' : 'Créer le client'}
        </button>
      </div>
    </div>
  );
};

// ============================================
// Main Wizard
// ============================================
export const CreateDossierWizard: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(INITIAL_DATA);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pendingRoles, setPendingRoles] = useState<string[]>(['demandeur']);
  const [isBillingContact, setIsBillingContact] = useState(true);
  const searchTimeout = useRef<NodeJS.Timeout>();

  // Dossier import state
  const [importDossierQuery, setImportDossierQuery] = useState('');
  const [importedContacts, setImportedContacts] = useState<Array<{ roles: string[]; nom_representant: string; prenom: string; email_1: string; tel_mobile: string; k_id_contact: string }>>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');

  const handleImportDossier = async () => {
    const id = importDossierQuery.trim().replace('#', '');
    if (!id || isNaN(Number(id))) { setImportError('Numéro de dossier invalide'); return; }
    setImportLoading(true);
    setImportError('');
    setImportedContacts([]);
    try {
      const res: any = await apiClient.get(`/api/fm/affaire/${id}/contacts`);
      if (res.success && res.data?.contacts?.length > 0) {
        // Filter out empty contacts (no name, no email, no phone)
        const valid = res.data.contacts.filter((c: any) =>
          c.k_id_contact || c.nom_representant || c.prenom || c.email_1
        );
        if (valid.length > 0) {
          setImportedContacts(valid);
        } else {
          setImportError('Aucun contact valide trouvé pour ce dossier');
        }
      } else {
        setImportError('Aucun contact trouvé pour ce dossier');
      }
    } catch {
      setImportError('Dossier non trouvé');
    } finally { setImportLoading(false); }
  };

  const resolveContact = async (contact: typeof importedContacts[0]): Promise<LinkedContact | null> => {
    let customer: CustomerResult | null = null;

    if (contact.k_id_contact) {
      try {
        const res: any = await apiClient.get(`/api/customers/by-fm-id/${contact.k_id_contact}`);
        if (res.success && res.data) customer = res.data;
      } catch { /* ignore */ }
    }
    if (!customer) {
      const searches = [contact.email_1, [contact.prenom, contact.nom_representant].filter(Boolean).join(' ')].filter(Boolean);
      for (const q of searches) {
        if (customer) break;
        try {
          const res: any = await apiClient.get('/api/customers/search', { params: { q, limit: 1 } });
          if (res.success && res.data.length > 0) customer = res.data[0];
        } catch { /* ignore */ }
      }
    }
    if (!customer) return null;

    const rawRoles = (contact.roles || []).map(r => r.toLowerCase().trim());
    const isBilling = rawRoles.some(r => r === 'facture' || r === 'facture mensuelle');
    const isSite = rawRoles.some(r => r === 'contact visite');
    const FLAG_ROLES = ['facture', 'facture mensuelle', 'contact visite', 'siege'];
    const FM_ROLE_MAP: Record<string, string> = {
      'intermediaire': 'intermediaire', 'demandeur': 'demandeur',
      'proprietaire': 'proprietaire', 'apporteur': 'apporteur',
      'notaire': 'notaire', 'banquier': 'banque', 'banque': 'banque',
      'expert': 'expert', 'locataire': 'locataire',
      'courtier': 'intermediaire', 'agent immo': 'intermediaire',
      'avocat': 'notaire', 'analyste': 'expert', 'inspecteur': 'expert',
    };
    const cleanRoles = rawRoles
      .filter(r => !FLAG_ROLES.includes(r))
      .map(r => FM_ROLE_MAP[r] || r)
      .filter((r, i, arr) => arr.indexOf(r) === i);
    if (cleanRoles.length === 0) cleanRoles.push('demandeur');

    return { customer, roles: cleanRoles, is_billing_contact: isBilling, is_site_contact: isSite };
  };

  const importContact = async (contact: typeof importedContacts[0]) => {
    const resolved = await resolveContact(contact);
    if (resolved && !data.contacts.some(c => c.customer.id === resolved.customer.id)) {
      setData(d => ({
        ...d,
        contacts: [...d.contacts, resolved]
      }));
    }
  };

  const importAllContacts = async () => {
    const resolved: LinkedContact[] = [];
    const existingIds = new Set(data.contacts.map(c => c.customer.id));

    for (const c of importedContacts) {
      const r = await resolveContact(c);
      if (r && !existingIds.has(r.customer.id)) {
        resolved.push(r);
        existingIds.add(r.customer.id);
      }
    }

    if (resolved.length > 0) {
      setData(d => ({
        ...d,
        contacts: [...d.contacts, ...resolved]
      }));
    }
  };

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await apiClient.get('/api/customers/search', { params: { q, limit: 10 } });
        setSearchResults(res.success ? res.data : []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 250);
  };

  const addContact = (customer: CustomerResult) => {
    // Don't add if already linked
    if (data.contacts.some(c => c.customer.id === customer.id)) return;
    setData(d => ({
      ...d,
      contacts: [...d.contacts, { customer, roles: [...pendingRoles], is_billing_contact: isBillingContact, is_site_contact: false }]
    }));
    setSearchQuery('');
    setSearchResults([]);
    setPendingRoles([]);
    setIsBillingContact(false);
    setShowCreateForm(false);
  };

  const removeContact = (idx: number) => {
    setData(d => ({ ...d, contacts: d.contacts.filter((_, i) => i !== idx) }));
  };

  const toggleRole = (idx: number, role: string) => {
    setData(d => ({
      ...d,
      contacts: d.contacts.map((c, i) => {
        if (i !== idx) return c;
        const has = c.roles.includes(role);
        return { ...c, roles: has ? c.roles.filter(r => r !== role) : [...c.roles, role] };
      })
    }));
  };

  const toggleBilling = (idx: number) => {
    setData(d => ({
      ...d,
      contacts: d.contacts.map((c, i) => ({
        ...c,
        is_billing_contact: i === idx ? !c.is_billing_contact : false
      }))
    }));
  };

  const toggleSiteContact = (idx: number) => {
    setData(d => ({
      ...d,
      contacts: d.contacts.map((c, i) => ({
        ...c,
        is_site_contact: i === idx ? !c.is_site_contact : false
      }))
    }));
  };

  const handleCreate = async () => {
    if (data.contacts.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const contacts = data.contacts.flatMap(c =>
        c.roles.map(role => ({
          customer_id: c.customer.id,
          role,
          is_billing_contact: c.is_billing_contact,
          is_site_contact: c.is_site_contact,
        }))
      );

      const res: any = await apiClient.post('/api/dossiers', {
        type: data.type,
        sub_type: data.sub_type,
        property_street: data.property_street,
        property_number: data.property_number,
        property_postcode: data.property_postcode,
        property_city: data.property_city,
        property_type: data.property_type,
        contacts,
      });

      if (res.success) navigate(`/admin/affaire/${res.data.id}`);
      else setError(res.message || 'Erreur lors de la création');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally { setCreating(false); }
  };

  const canNext = () => {
    switch (step) {
      case 1: return data.contacts.length > 0 && data.contacts.some(c => c.roles.length > 0);
      case 2: return !!data.property_city;
      case 3: return !!data.type;
      case 4: return true;
      default: return false;
    }
  };

  const togglePendingRole = (role: string) => {
    setPendingRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  return (
    <div className="create-dossier-page">
      {/* Header */}
      <div className="create-dossier-header">
        <button onClick={() => navigate(-1)} className="aff-detail-back-btn">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="create-dossier-title">Nouveau dossier</h1>
      </div>

      {/* Stepper */}
      <div className="create-dossier-stepper">
        {STEPS.map((s, i) => (
          <div key={s.id} className={`create-dossier-step ${step >= s.id ? 'active' : ''} ${step === s.id ? 'current' : ''}`}>
            <div className="create-dossier-step-dot">
              {step > s.id ? <CheckIcon className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
            </div>
            <span className="create-dossier-step-label">{s.label}</span>
            {i < STEPS.length - 1 && <div className="create-dossier-step-line" />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="create-dossier-content">

        {/* ============ Step 1: Contacts (two-column) ============ */}
        {step === 1 && (
          <div className="create-dossier-step1-columns">
            {/* LEFT: Search / Create / Import */}
            <div className="create-dossier-step1-left">
              <div className="create-dossier-card">
                <h2>Rechercher un contact</h2>

                {/* Preset roles */}
                <div className="create-dossier-preset-roles">
                  <span className="create-dossier-preset-roles-label">Rôle(s) à assigner :</span>
                  <div className="create-dossier-role-chips">
                    {CONTACT_ROLES.map(r => (
                      <button key={r.value} className={`create-dossier-role-chip ${pendingRoles.includes(r.value) ? 'active' : ''}`} onClick={() => togglePendingRole(r.value)}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search */}
                {!showCreateForm && (
                  <>
                    <div className="create-dossier-search">
                      <MagnifyingGlassIcon className="w-4 h-4" />
                      <input type="text" placeholder="Nom, email, téléphone, TVA..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} autoFocus />
                      {searching && <span className="create-dossier-search-spinner" />}
                    </div>

                    {searchResults.length > 0 && (
                      <div className="create-dossier-results">
                        {searchResults.map(c => (
                          <button key={c.id} className={`create-dossier-result ${data.contacts.some(lc => lc.customer.id === c.id) ? 'disabled' : ''}`} onClick={() => addContact(c)} disabled={data.contacts.some(lc => lc.customer.id === c.id)}>
                            <div>
                              <span className="create-dossier-result-name">{customerDisplayName(c)}</span>
                              {c.type === 'company' && <span className="create-dossier-result-badge">Société</span>}
                            </div>
                            <div className="create-dossier-result-meta-row">
                              {c.email && <span>{c.email}</span>}
                              {c.phone && <span>{c.phone}</span>}
                              {c.address_city && <span>{c.address_city}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                      <div className="create-dossier-no-results">Aucun résultat pour "{searchQuery}"</div>
                    )}

                    <button onClick={() => setShowCreateForm(true)} className="create-dossier-create-btn">
                      <PlusIcon className="w-4 h-4" /> Créer un nouveau client
                    </button>
                  </>
                )}

                {showCreateForm && (
                  <InlineCreateForm onCreated={(c) => addContact(c)} onCancel={() => setShowCreateForm(false)} />
                )}
              </div>

              {/* Import from dossier */}
              <div className="create-dossier-card">
                <h2>Importer depuis un dossier</h2>
                <div className="create-dossier-import-row">
                  <input type="text" placeholder="N° dossier (ex: 71000)" value={importDossierQuery} onChange={e => setImportDossierQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleImportDossier()} className="create-dossier-import-input" />
                  <button onClick={handleImportDossier} disabled={importLoading || !importDossierQuery.trim()} className="create-dossier-nav-btn create-dossier-nav-btn--secondary">
                    {importLoading ? '...' : 'Chercher'}
                  </button>
                </div>

                {importError && <div className="create-dossier-import-error">{importError}</div>}

                {importedContacts.length > 0 && (
                  <div className="create-dossier-import-results">
                    <div className="create-dossier-import-results-header">
                      <span>{importedContacts.length} contact(s)</span>
                      <button onClick={importAllContacts} className="create-dossier-import-all-btn">Tout importer</button>
                    </div>
                    {importedContacts.map((c, i) => {
                      const name = [c.prenom, c.nom_representant].filter(Boolean).join(' ') || '-';
                      const alreadyAdded = data.contacts.some(lc =>
                        (lc.customer.email && lc.customer.email === c.email_1) ||
                        (lc.customer.last_name && lc.customer.last_name === c.nom_representant)
                      );
                      return (
                        <div key={i} className={`create-dossier-import-contact ${alreadyAdded ? 'imported' : ''}`}>
                          <div className="create-dossier-import-contact-info">
                            {c.roles?.map(r => <span key={r} className="create-dossier-import-contact-role">{r}</span>)}
                            <span className="create-dossier-import-contact-name">{name}</span>
                            {c.email_1 && <span className="create-dossier-import-contact-meta">{c.email_1}</span>}
                          </div>
                          <button onClick={() => importContact(c)} disabled={alreadyAdded} className="create-dossier-import-contact-btn">
                            {alreadyAdded ? 'Ajouté' : 'Importer'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Linked contacts */}
            <div className="create-dossier-step1-right">
              <div className="create-dossier-card" style={{ position: 'sticky', top: 24 }}>
                <h2>Contacts liés ({data.contacts.length})</h2>

                {data.contacts.length === 0 ? (
                  <div className="create-dossier-step1-empty">
                    <UserIcon className="w-8 h-8" />
                    <p>Aucun contact ajouté</p>
                    <span>Recherchez ou importez des contacts depuis la colonne de gauche</span>
                  </div>
                ) : (
                  <div className="create-dossier-contacts-list">
                    {data.contacts.map((c, idx) => (
                      <div key={c.customer.id} className="create-dossier-contact-card">
                        <div className="create-dossier-contact-card-header">
                          <div>
                            <span className="create-dossier-contact-card-name">{customerDisplayName(c.customer)}</span>
                            {c.customer.email && <span className="create-dossier-contact-card-email">{c.customer.email}</span>}
                          </div>
                          <button onClick={() => removeContact(idx)} className="create-dossier-contact-card-remove">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="create-dossier-role-chips">
                          {CONTACT_ROLES.map(r => (
                            <button key={r.value} className={`create-dossier-role-chip ${c.roles.includes(r.value) ? 'active' : ''}`} onClick={() => toggleRole(idx, r.value)}>
                              {r.label}
                            </button>
                          ))}
                        </div>

                        <div className="create-dossier-contact-toggles">
                          <label className="create-dossier-billing-toggle">
                            <input type="checkbox" checked={c.is_billing_contact} onChange={() => toggleBilling(idx)} />
                            <span>Contact de facturation</span>
                          </label>
                          <label className="create-dossier-billing-toggle create-dossier-site-toggle">
                            <input type="checkbox" checked={c.is_site_contact} onChange={() => toggleSiteContact(idx)} />
                            <span>Contact sur place (visite)</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============ Step 2: Property ============ */}
        {step === 2 && (
          <div className="create-dossier-card">
            <h2>Adresse du bien</h2>
            <div className="create-dossier-form">
              <div className="create-dossier-form-row-split">
                <div>
                  <label>Rue</label>
                  <input value={data.property_street} onChange={e => setData(d => ({ ...d, property_street: e.target.value }))} placeholder="Rue de Test" />
                </div>
                <div style={{ maxWidth: 100 }}>
                  <label>N°</label>
                  <input value={data.property_number} onChange={e => setData(d => ({ ...d, property_number: e.target.value }))} placeholder="42" />
                </div>
              </div>
              <div className="create-dossier-form-row-split">
                <div style={{ maxWidth: 120 }}>
                  <label>Code postal</label>
                  <input value={data.property_postcode} onChange={e => setData(d => ({ ...d, property_postcode: e.target.value }))} placeholder="4000" />
                </div>
                <div>
                  <label>Ville</label>
                  <input value={data.property_city} onChange={e => setData(d => ({ ...d, property_city: e.target.value }))} placeholder="Liège" />
                </div>
              </div>
              <div>
                <label>Type de bien</label>
                <select value={data.property_type} onChange={e => setData(d => ({ ...d, property_type: e.target.value }))}>
                  {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ============ Step 3: Mission ============ */}
        {step === 3 && (
          <div className="create-dossier-card">
            <h2>Type de mission</h2>
            <div className="create-dossier-form">
              <div>
                <label>Type</label>
                <select value={data.type} onChange={e => setData(d => ({ ...d, type: e.target.value }))}>
                  {MISSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label>Sous-type</label>
                <input value={data.sub_type} onChange={e => setData(d => ({ ...d, sub_type: e.target.value }))} placeholder="FULL EXPERTISE" />
              </div>
              <div>
                <label>Notes (optionnel)</label>
                <textarea value={data.notes} onChange={e => setData(d => ({ ...d, notes: e.target.value }))} rows={3} placeholder="Instructions spéciales..." />
              </div>
            </div>
          </div>
        )}

        {/* ============ Step 4: Confirm ============ */}
        {step === 4 && (
          <div className="create-dossier-card">
            <h2>Résumé</h2>
            <div className="create-dossier-summary">
              <div className="create-dossier-summary-section">
                <h3>Contacts ({data.contacts.length})</h3>
                {data.contacts.map((c, i) => (
                  <div key={i} className="create-dossier-summary-contact">
                    <strong>{customerDisplayName(c.customer)}</strong>
                    <span className="create-dossier-summary-roles">
                      {c.roles.map(r => r.toUpperCase()).join(', ')}
                      {c.is_billing_contact && ' — FACTURATION'}
                      {c.is_site_contact && ' — CONTACT SUR PLACE'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="create-dossier-summary-section">
                <h3>Bien</h3>
                <p>{[data.property_number, data.property_street].filter(Boolean).join(' ')}</p>
                <p>{[data.property_postcode, data.property_city].filter(Boolean).join(' ')}</p>
                <p className="text-sm text-gray-400 capitalize">{data.property_type}</p>
              </div>
              <div className="create-dossier-summary-section">
                <h3>Mission</h3>
                <p>{MISSION_TYPES.find(t => t.value === data.type)?.label || data.type}</p>
                {data.sub_type && <p className="text-sm text-gray-400">{data.sub_type}</p>}
              </div>
            </div>
            {error && <div className="create-dossier-error">{error}</div>}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="create-dossier-nav">
        {step > 1 && (
          <button onClick={() => setStep(s => s - 1)} className="create-dossier-nav-btn create-dossier-nav-btn--secondary">
            <ArrowLeftIcon className="w-4 h-4" />
            Précédent
          </button>
        )}
        <div className="flex-1" />
        {step < 4 ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="create-dossier-nav-btn create-dossier-nav-btn--primary">
            Suivant
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={handleCreate} disabled={creating || !canNext()} className="create-dossier-nav-btn create-dossier-nav-btn--primary">
            {creating ? 'Création...' : 'Créer le dossier'}
            <CheckIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
